import { useEffect, useState } from "react";
import { Building2, Hash, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { BaseGlyph } from "@/components/bases/baseUtils";

/** Debounced smart-search suggestions (installations, cities, neighborhoods, ZIPs). */
export function useSuggestions(term, enabled = true) {
  const [res, setRes] = useState({ bases: [], places: [] });
  useEffect(() => {
    const q = term.trim();
    if (!enabled || q.length < 2) {
      setRes({ bases: [], places: [] });
      return undefined;
    }
    let alive = true;
    const t = setTimeout(() => {
      api.get("/suggest", { params: { q } })
        .then((r) => alive && setRes({ bases: r.data?.bases ?? [], places: r.data?.places ?? [] }))
        .catch(() => alive && setRes({ bases: [], places: [] }));
    }, 160);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [term, enabled]);
  return res;
}

const PLACE_ICONS = { City: Building2, Neighborhood: MapPin, ZIP: Hash };
const rowCls = "flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-neutral-100";

export const SuggestionList = ({ bases, places, onBase, onPlace }) => {
  if (!bases.length && !places.length) return null;
  return (
    <div className="absolute inset-x-0 top-full z-30 mt-1 max-h-[300px] overflow-y-auto bg-white py-2 text-ink shadow-2xl sm:max-h-[360px]" data-testid="smart-search-suggestions">
      {bases.length > 0 && <p className="px-4 pb-1 pt-1 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Installations</p>}
      {bases.map((b) => (
        <button key={b.id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => onBase(b)} className={rowCls} data-testid={`suggest-base-${b.id}`}>
          <BaseGlyph base={b} size="h-7 w-7" />
          <span className="flex-1"><span className="block text-[15px]">{b.name}</span><span className="text-[12px] text-neutral-500">{b.branch} · {b.city} · homes by distance</span></span>
        </button>
      ))}
      {places.length > 0 && <p className="px-4 pb-1 pt-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Places with homes</p>}
      {places.map((p) => {
        const Icon = PLACE_ICONS[p.kind] || MapPin;
        return (
          <button key={`${p.kind}-${p.label}`} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => onPlace(p)} className={rowCls}
            data-testid={`suggest-place-${p.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <span className="grid h-7 w-7 place-items-center rounded-full bg-neutral-100"><Icon className="h-3.5 w-3.5" /></span>
            <span className="flex-1 text-[15px]">{p.label} <span className="text-[12px] text-neutral-500">· {p.kind}</span></span>
            <span className="font-mono text-[11px] text-neutral-500">{p.count} homes</span>
          </button>
        );
      })}
    </div>
  );
};
